/**
 * Schedule Page Route
 * Feature: 005-clinic-scheduling (T020, T023)
 *
 * Calendar page displaying all clinic appointments with professional filter.
 */

import { useCallback, useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { MainLayout } from '@/components/layout'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useAuth } from '@/features/auth'
import { ScheduleCalendar } from '@/features/scheduling/components/ScheduleCalendar'
import type { SlotClickInfo } from '@/features/scheduling/components/ScheduleCalendar'
import { CalendarToolbar } from '@/features/scheduling/components/CalendarToolbar'
import { AppointmentDetailModal } from '@/features/scheduling/components/AppointmentDetailModal'
import { NewAppointmentModal } from '@/features/scheduling/components/NewAppointmentModal'
import type { CalendarEventExtendedProps } from '@/features/scheduling/types/scheduling.types'

export const Route = createFileRoute('/_authenticated/schedule')({
  component: SchedulePage,
})

function SchedulePage() {
  useDocumentTitle('Agenda')
  const { currentOrganization, isAdmin, loading, userProfile } = useAuth()

  // Default filter: admins see all, professionals see their own
  const [professionalFilter, setProfessionalFilter] = useState<string | null>(
    null
  )
  const [hasCustomProfessionalFilter, setHasCustomProfessionalFilter] =
    useState(false)
  const [selectedEvent, setSelectedEvent] =
    useState<CalendarEventExtendedProps | null>(null)
  const [slotClick, setSlotClick] = useState<SlotClickInfo | null>(null)

  useEffect(() => {
    setHasCustomProfessionalFilter(false)
  }, [currentOrganization?.id])

  useEffect(() => {
    if (loading || hasCustomProfessionalFilter) return

    setProfessionalFilter(isAdmin ? null : (userProfile?.id ?? null))
  }, [
    currentOrganization?.id,
    hasCustomProfessionalFilter,
    isAdmin,
    loading,
    userProfile?.id,
  ])

  const handleProfessionalChange = useCallback((id: string | null) => {
    setHasCustomProfessionalFilter(true)
    setProfessionalFilter(id)
  }, [])

  const handleEventClick = (eventProps: CalendarEventExtendedProps) => {
    setSelectedEvent(eventProps)
  }

  const handleSlotClick = (info: SlotClickInfo) => {
    setSlotClick(info)
  }

  return (
    <MainLayout title="Agenda" subtitle="Visualize e gerencie os atendimentos da clinica">
      <CalendarToolbar
        selectedProfessionalId={professionalFilter}
        onProfessionalChange={handleProfessionalChange}
      />

      <ScheduleCalendar
        onEventClick={handleEventClick}
        onSlotClick={handleSlotClick}
        professionalFilter={professionalFilter}
      />

      <AppointmentDetailModal
        event={selectedEvent}
        open={!!selectedEvent}
        onOpenChange={(open) => {
          if (!open) setSelectedEvent(null)
        }}
      />

      <NewAppointmentModal
        open={!!slotClick}
        onOpenChange={(open) => {
          if (!open) setSlotClick(null)
        }}
        initialDate={slotClick?.date ?? ''}
        initialStartTime={slotClick?.startTime ?? ''}
        initialEndTime={slotClick?.endTime ?? ''}
      />
    </MainLayout>
  )
}
